'use client';
import React from 'react';
import { Skeleton } from '@benzinga/core-ui';
import { CallToAction } from '@benzinga/ui';
import { selectCampaignTargets } from '../../utils/targeting';
import { isMobile } from '@benzinga/device-utils';
import { WordpressWidget } from '@benzinga/content-manager';
import { Impression, useGeo } from '@benzinga/analytics';
import { MoneyBlocks } from '../MoneyBlocks';
import { sanitizeHTML } from '@benzinga/frontend-utils';

export interface PlaceholderProps {
  campaign?: any;
  widget?: any;
  targets: any[];
  type: string;
}

export const Placeholder: React.FC<PlaceholderProps> = ({ campaign, targets, type, widget }) => {
  const [label] = React.useState(type === 'primary' ? 'Primary CTA' : 'Secondary CTA');
  const [isLoading, setIsLoading] = React.useState(true);
  const [currentCampaign, setCurrentCampaign] = React.useState(campaign);
  const [currentWidget, setCurrentWidget] = React.useState<WordpressWidget>(widget);
  const geo = useGeo();

  React.useEffect(() => {
    if (geo === null) return;
    const campaignType = type ? type : 'primary';
    const options = selectCampaignTargets(targets, geo, isMobile());
    // console.log("OPTIONS: ", options);
    const placement = options[campaignType];
    if (!placement && targets[campaignType]?.widget) {
      setCurrentWidget(targets[campaignType]?.widget);
    } else if (placement?.type === 'widget') {
      setCurrentWidget(placement);
    } else {
      setCurrentCampaign(placement?.campaign);
    }
    setIsLoading(false);
  }, [targets, type, geo]);

  const renderPlaceHolder = () => {
    return (
      <Impression
        campaign_id={currentCampaign?.id ?? currentCampaign?.utm_action}
        tag={currentCampaign?.impression_tag}
        unit_type={`${label} - Campaign Placeholder`}
      >
        <div className={`cta-placeholder-${type}`}>
          {currentWidget?.blocks && <MoneyBlocks blocks={currentWidget.blocks} />}
          {currentWidget && currentWidget?.post_content && (
            <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(currentWidget.post_content) }} />
          )}
          {currentCampaign && <CampaignPlaceholderCallToAction {...currentCampaign} label={label} />}
        </div>
      </Impression>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-[300px] md:min-h-[186px] my-4">
        <Skeleton active={true} variant="card" />
      </div>
    );
  }

  return !currentCampaign && !currentWidget ? <div /> : renderPlaceHolder();
};

export const CampaignPlaceholderCallToAction = ({
  cta_description,
  cta_text,
  cta_title,
  disclosure,
  go_link_dofollow,
  id,
  image_url,
  label,
  url,
  utm_action,
}) => {
  return (
    <CallToAction
      button_text={cta_text}
      description={cta_description}
      disclosure={disclosure}
      floating={false}
      go_link_dofollow={go_link_dofollow}
      id={id}
      image_url={image_url}
      show_bz_badge={true}
      title={cta_title}
      url={url}
      utm_action={utm_action ? utm_action : cta_title}
      utm_category="Lead Click"
      utm_label={label}
    />
  );
};
